// Input formatting utilities for consistent data entry across the application
import React from 'react';
import { safeParseFloat, VALIDATION_RULES } from './inputValidation';

export interface InputFormatOptions {
  type: 'currency' | 'percent' | 'number' | 'date';
  allowNegative?: boolean;
  decimalPlaces?: number;
}

export const formatInputValue = (value: string, options: InputFormatOptions): string => {
  if (!value) return '';

  switch (options.type) {
    case 'currency':
      return formatCurrency(value, options.allowNegative, options.decimalPlaces);
    case 'percent':
      return formatPercent(value, options.decimalPlaces);
    case 'number':
      return formatNumber(value, options.allowNegative, options.decimalPlaces);
    case 'date':
      return formatDate(value);
    default:
      return value;
  }
};

export const parseInputValue = (value: string, options: InputFormatOptions): number | string => {
  if (!value) return '';

  switch (options.type) {
    case 'currency':
      return parseCurrency(value);
    case 'percent':
      return parsePercent(value);
    case 'number':
      return parseNumber(value);
    case 'date':
      return parseDate(value);
    default:
      return value;
  }
};

// Currency formatting: #,##0.00
const formatCurrency = (value: string, allowNegative: boolean = false, decimalPlaces: number = 2): string => {
  // Remove all non-numeric characters except decimal point
  let cleanValue = value.replace(/[^\d.-]/g, '');
  
  // Handle negative numbers
  const isNegative = cleanValue.startsWith('-');
  if (isNegative && !allowNegative) {
    cleanValue = cleanValue.substring(1);
  }
  
  // Convert to number and format
  const numValue = parseFloat(cleanValue) || 0;
  const formatted = new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping: true
  }).format(Math.abs(numValue));
  
  return isNegative && allowNegative ? `-${formatted}` : formatted;
};

const parseCurrency = (value: string): number => {
  // Remove all formatting and convert to number with validation
  const cleanValue = value.replace(/[^\d.-]/g, '');
  const validation = safeParseFloat(cleanValue, VALIDATION_RULES.CURRENCY, 0);
  return validation.value;
};

// Percent formatting: 0.00%
const formatPercent = (value: string, decimalPlaces: number = 2): string => {
  // Remove all non-numeric characters except decimal point
  let cleanValue = value.replace(/[^\d.-]/g, '');
  
  // Convert to number and format
  const numValue = parseFloat(cleanValue) || 0;
  const formatted = numValue.toFixed(decimalPlaces);
  
  return `${formatted}%`;
};

const parsePercent = (value: string): number => {
  // Remove % and convert to number with validation
  const cleanValue = value.replace(/[^\d.-]/g, '');
  const validation = safeParseFloat(cleanValue, VALIDATION_RULES.PERCENTAGE, 0);
  return validation.value;
};

// Number formatting: 0,000.00
const formatNumber = (value: string, allowNegative: boolean = false, decimalPlaces: number = 0): string => {
  // Remove all non-numeric characters except decimal point
  let cleanValue = value.replace(/[^\d.-]/g, '');
  
  // Handle negative numbers
  const isNegative = cleanValue.startsWith('-');
  if (isNegative && !allowNegative) {
    cleanValue = cleanValue.substring(1);
  }
  
  // Convert to number and format
  const numValue = parseFloat(cleanValue) || 0;
  const formatted = new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping: true
  }).format(Math.abs(numValue));
  
  return isNegative && allowNegative ? `-${formatted}` : formatted;
};

const parseNumber = (value: string): number => {
  // Remove all formatting and convert to number
  const cleanValue = value.replace(/[^\d.-]/g, '');
  return parseFloat(cleanValue) || 0;
};

// Date formatting: YYYY-MMM-DD
const formatDate = (value: string): string => {
  // Remove all non-numeric characters
  const cleanValue = value.replace(/[^\d]/g, '');
  
  if (cleanValue.length === 0) return '';
  if (cleanValue.length <= 4) return cleanValue;
  if (cleanValue.length <= 6) return `${cleanValue.slice(0, 4)}-${cleanValue.slice(4)}`;
  
  const year = cleanValue.slice(0, 4);
  const month = cleanValue.slice(4, 6);
  const day = cleanValue.slice(6, 8);
  
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const monthIndex = parseInt(month) - 1;
  const monthName = monthIndex >= 0 && monthIndex < 12 ? monthNames[monthIndex] : month;
  
  return `${year}-${monthName}-${day}`;
};

const parseDate = (value: string): string => {
  // Convert formatted date back to YYYY-MM-DD format
  const parts = value.split('-');
  if (parts.length !== 3) return value;
  
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  
  // Convert month name to number
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  let monthNum = month;
  const monthIndex = monthNames.indexOf(month);
  if (monthIndex !== -1) {
    monthNum = String(monthIndex + 1).padStart(2, '0');
  }
  
  return `${year}-${monthNum}-${day}`;
};

// React hook for formatted inputs
export const useFormattedInput = (options: InputFormatOptions) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const formattedValue = formatInputValue(value, options);
    e.target.value = formattedValue;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const formattedValue = formatInputValue(value, options);
    e.target.value = formattedValue;
  };

  return { handleChange, handleBlur };
};

// Predefined formatters for common use cases
export const formatters = {
  currency: (allowNegative: boolean = false, decimalPlaces: number = 0) => ({
    type: 'currency' as const,
    allowNegative,
    decimalPlaces
  }),
  percent: (decimalPlaces: number = 2) => ({ type: 'percent' as const, decimalPlaces }),
  number: (allowNegative: boolean = false, decimalPlaces: number = 0) => ({
    type: 'number' as const,
    allowNegative,
    decimalPlaces
  }),
  date: () => ({ type: 'date' as const })
};

/**
 * Format currency with color coding for financial tables
 * RBC Bank Standard Colors:
 * - Income/Gains: GREEN (#059669)
 * - Expenses/Losses: RED (#dc2626)
 * - Neutral: No color
 */
export const formatCurrencyWithColor = (
  value: number,
  colorType: 'positive' | 'negative' | 'neutral' = 'neutral',
  formatCurrency: (value: number) => string
): React.ReactElement => {
  const formatted = formatCurrency(value);
  const colors = {
    positive: '#059669',  // Green for income/gains
    negative: '#dc2626',  // Red for expenses/losses
    neutral: 'inherit'    // Default text color
  };
  return React.createElement('span', { style: { color: colors[colorType] } }, formatted);
}; 

/** Today as YYYY-MM-DD in the user's own timezone (toISOString() would roll to tomorrow after 8pm Eastern) */
export const todayLocalIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/** Display a YYYY-MM-DD string without the UTC shift that new Date('2026-09-19') introduces */
export const formatIsoDate = (iso: string): string => {
  return new Date(`${iso}T00:00:00`).toLocaleDateString();
};
